// import React, { createContext, useContext, useState, useEffect } from 'react';
// import { useLocation } from 'react-router-dom';
// import apiClient from '../utils/apiClient';
// import AdminWelcomeModal from './AdminWelcomeModal';

// const OnboardingContext = createContext();

// export const OnboardingProvider = ({ children }) => {
//     const [runTour, setRunTour] = useState(false);
//     const [stepIndex, setStepIndex] = useState(0);
//     const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
//     const [showWelcomeModal, setShowWelcomeModal] = useState(false);
//     const [isLoading, setIsLoading] = useState(true);
//     const location = useLocation();

//     useEffect(() => {
//         const fetchOnboardingStatus = async () => {
//             // Only show on Dashboard paths to avoid being intrusive
//             const isAdminDashboard = location.pathname === '/dashboard/admin';

//             if (!isAdminDashboard || hasCompletedOnboarding) {
//                 setIsLoading(false);
//                 return;
//             }

//             try {
//                 const token = localStorage.getItem('token');
//                 const user = JSON.parse(localStorage.getItem('user') || '{}');
//                 const role = user.roleName;

//                 // Only show onboarding for Admins
//                 if (!token || role !== 'admin') {
//                     setIsLoading(false);
//                     return;
//                 }

//                 // Check actual progress percentage
//                 const response = await apiClient.get('/onboarding/progress-metrics');
//                 if (response.data) {
//                     const isComplete = response.data.is_trial_activation_complete === true || response.data.progress_percentage === 100;
//                     setHasCompletedOnboarding(isComplete);
                    
//                     // Show modal if progress is not 100%
//                     if (!isComplete) {
//                         setShowWelcomeModal(true);
//                     }
//                 }
//             } catch (error) {
//                 console.error("Failed to fetch onboarding status:", error);
//                 // Fallback: If we can't confirm completion, we check local storage
//                 const localStatus = localStorage.getItem('hasCompletedOnboarding') === 'true';
//                 if (localStatus) {
//                     setHasCompletedOnboarding(true);
//                 } else {
//                     setShowWelcomeModal(true);
//                 }
//             } finally {
//                 setIsLoading(false);
//             }
//         };

//         fetchOnboardingStatus();
//     }, [location.pathname, hasCompletedOnboarding]);

//     const completeOnboarding = async () => {
//         try {
//             await apiClient.post('/onboarding/track', { step_column: 'is_dismissed' });
//             setHasCompletedOnboarding(true);
//             setShowWelcomeModal(false);
//             setRunTour(false);
//             localStorage.setItem('hasCompletedOnboarding', 'true');
//         } catch (error) {
//             console.error("Failed to mark onboarding as complete:", error);
//         }
//     };

//     const forceCloseWelcomeModal = (shouldStartTour = false) => {
//         setShowWelcomeModal(false);
//         if (shouldStartTour) {
//             setStepIndex(0);
//             setRunTour(true);
//         }
//     };

//     return (
//         <OnboardingContext.Provider value={{ 
//             runTour, 
//             setRunTour, 
//             stepIndex, 
//             setStepIndex, 
//             hasCompletedOnboarding, 
//             setHasCompletedOnboarding,
//             showWelcomeModal,
//             setShowWelcomeModal,
//             forceCloseWelcomeModal,
//             completeOnboarding,
//             isLoading
//         }}>
//             {showWelcomeModal && <AdminWelcomeModal />}
//             {children}
//         </OnboardingContext.Provider>
//     );
// };

// export const useOnboarding = () => {
//     const context = useContext(OnboardingContext);
//     if (!context) {
//         throw new Error('useOnboarding must be used within an OnboardingProvider');
//     }
//     return context;
// };


import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import AdminWelcomeModal from './AdminWelcomeModal';

const OnboardingContext = createContext();

export const OnboardingProvider = ({ children }) => {
    const [runTour, setRunTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation();

    useEffect(() => {
        const fetchOnboardingStatus = async () => {
            // Only show on Dashboard paths to avoid being intrusive
            const isAdminDashboard = location.pathname === '/dashboard/admin';

            if (!isAdminDashboard || hasCompletedOnboarding) {
                setIsLoading(false);
                return;
            }

            try {
                const token = localStorage.getItem('token');
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                const role = user.roleName;

                // THE GATEKEEPER: Only normal Admins get the onboarding tour
                if (!token || role !== 'admin') {
                    setIsLoading(false);
                    return;
                }

                // Check actual progress percentage
                const response = await apiClient.get('/onboarding/progress-metrics');
                if (response.data) {
                    const isComplete = response.data.is_trial_activation_complete === true || response.data.progress_percentage === 100;
                    setHasCompletedOnboarding(isComplete);
                    
                    // Check if the user snoozed the modal recently
                    const snoozeTimestamp = localStorage.getItem('onboardingSnoozedUntil');
                    const isSnoozed = snoozeTimestamp && Date.now() < parseInt(snoozeTimestamp, 10);

                    // Show modal if progress is not 100% AND they haven't snoozed it for today
                    if (!isComplete && !isSnoozed) {
                        setShowWelcomeModal(true);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch onboarding status:", error);
                
                // Fallback: If we can't confirm completion, we check local storage
                const localStatus = localStorage.getItem('hasCompletedOnboarding') === 'true';
                if (localStatus) {
                    setHasCompletedOnboarding(true);
                } else {
                    // Also respect snooze in the fallback
                    const snoozeTimestamp = localStorage.getItem('onboardingSnoozedUntil');
                    const isSnoozed = snoozeTimestamp && Date.now() < parseInt(snoozeTimestamp, 10);
                    if (!isSnoozed) setShowWelcomeModal(true);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchOnboardingStatus();
    }, [location.pathname, hasCompletedOnboarding]);

    const completeOnboarding = async () => {
        try {
            await apiClient.post('/onboarding/track', { step_column: 'is_dismissed' });
            setHasCompletedOnboarding(true);
            setShowWelcomeModal(false);
            setRunTour(false);
            localStorage.setItem('hasCompletedOnboarding', 'true');
        } catch (error) {
            console.error("Failed to mark onboarding as complete:", error);
        }
    };

    const forceCloseWelcomeModal = (shouldStartTour = false) => {
        setShowWelcomeModal(false);
        if (shouldStartTour) {
            setStepIndex(0);
            setRunTour(true);
        }
    };

    // Sets a snooze timer until exactly midnight tonight (the start of tomorrow)
    const snoozeWelcomeModal = () => {
        const tomorrow = new Date();
        tomorrow.setHours(24, 0, 0, 0); // Sets time to 00:00:00 of the next day
        localStorage.setItem('onboardingSnoozedUntil', tomorrow.getTime().toString());
        setShowWelcomeModal(false);
    };

    return (
        <OnboardingContext.Provider value={{ 
            runTour, 
            setRunTour, 
            stepIndex, 
            setStepIndex, 
            hasCompletedOnboarding, 
            setHasCompletedOnboarding,
            showWelcomeModal,
            setShowWelcomeModal,
            forceCloseWelcomeModal,
            snoozeWelcomeModal,
            completeOnboarding,
            isLoading
        }}>
            {showWelcomeModal && <AdminWelcomeModal />}
            {children}
        </OnboardingContext.Provider>
    );
};

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error('useOnboarding must be used within an OnboardingProvider');
    }
    return context;
};
